import {useContext, useEffect, useMemo, useState} from 'react'
import {useIntl} from 'react-intl'
import {useSearchParams} from 'react-router-dom'
import {Checkbox, Empty, Flex, Modal, Result, Typography,} from 'antd'
import {CheckCircleFilled, ExclamationCircleOutlined} from '@ant-design/icons'
import {NavContext} from '../../../context/global_context'
import {getRequest, postRequest} from '../../../services/base'
import useDebouncedValue from '../../../utils/common_function'
import Button from '../../common/Button'
import InputText from '../../common/InputText'
import EllipsisTooltip from '../../common/EllipsisTooltip'
import {ProgresSpinner} from '../../common/ProgresSpinner'
import {emptyFolders, FOLDER_KEYS, formatFolderLabel} from '../constants'
import BoxTabs from '../BoxTabs'
import DownloadDeleteFileLogBtn from './download_logs_btn'
import SelectTrashFilesModal from './trash_files'

const { Text } = Typography

const DeleteFilesModal = ({
  vehicle = null,
  onClose = () => {},
  onDeleted = () => {},
}) => {
  const intl = useIntl()
  const [searchParams] = useSearchParams()
  const [, navDispatch] = useContext(NavContext)
  const [modal, contextHolder] = Modal.useModal()

  const [branchExists, setBranchExists] = useState(null)
  const [loadingDetails, setLoadingDetails] = useState(true)
  const [promotedFolderData, setPromotedFolderData] = useState({})
  const [isExists, setIsExists] = useState(false)
  const [trashFiles, setTrashFiles] = useState([])

  const [section, setSection] = useState(null)
  const [searchValue, setSearchValue] = useState('')
  const debouncedSearch = useDebouncedValue(searchValue, 500)
  const [selectedFolders, setSelectedFolders] = useState(emptyFolders())
  const [isReview, setReview] = useState(false)
  const [showTrashedModal, setShowTrashedModal] = useState(false)
  const [selectedTrashFiles, setSelectedTrashFiles] = useState([])

  const vehicleId = vehicle?.vehicle_id

  const loadDetails = async () => {
    setLoadingDetails(true)
    let res = await getRequest(
      `/api/delete/promoted-files/vehicle-details/${vehicleId}/`
    )
    if (res?.status === false) {
      navDispatch({
        type: 'notification',
        data: {
          notification_type: 'error',
          msg: intl.formatMessage({ id: 'promoteChanges.detailsLoadError' }),
        },
      })
      setLoadingDetails(false)
      return
    }
    const sectionData = res?.data?.section_data || {}
    setPromotedFolderData(sectionData)
    setIsExists(!!res?.data?.is_exists)
    setTrashFiles(res?.data?.trash_files || [])
    const firstSection = FOLDER_KEYS.find(
      (key) => sectionData?.[key]?.length > 0
    )
    setSection(firstSection || null)
    setLoadingDetails(false)
  }

  const validateBranch = async () => {
    let res = await getRequest(
      `/api/tracer/preview/validate/?data_version=${
        searchParams.get('data_version') ? searchParams.get('data_version') : ''
      }`
    )
    // A failed request is not the same as a branch that is absent: the else
    // below says "branch not available in git", which misreads an outage.
    if (res?.status === false) {
      navDispatch({
        type: 'notification',
        data: {
          notification_type: 'error',
          msg: intl.formatMessage({ id: 'promoteChanges.branchLoadError' }),
        },
      })
      return
    }
    setBranchExists(!!res?.data)
    if (!res?.data) {
      navDispatch({
        type: 'notification',
        data: {
          notification_type: 'error',
          msg: intl.formatMessage({ id: 'promoteChanges.branchNotAvailable' }),
        },
      })
    }
  }

  useEffect(() => {
    if (!vehicleId) return
    setBranchExists(null)
    setLoadingDetails(true)
    setSelectedFolders(emptyFolders())
    setReview(false)
    setSearchValue('')
    setSelectedTrashFiles([])
    validateBranch()
    loadDetails()
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [vehicleId])

  // Files shown for the active section (full list in select mode,
  // only selected files in review mode).
  const filesForSection = useMemo(() => {
    if (!section) return []
    return isReview
      ? selectedFolders?.[section] || []
      : promotedFolderData?.[section] || []
  }, [section, isReview, selectedFolders, promotedFolderData])

  const filteredFiles = useMemo(() => {
    const filtered = debouncedSearch
      ? filesForSection.filter((file_name) =>
          file_name.toLowerCase().includes(debouncedSearch.toLowerCase())
        )
      : filesForSection
    return [...filtered].sort()
  }, [filesForSection, debouncedSearch])

  const selectedFolderSet = useMemo(
    () => new Set(selectedFolders?.[section] || []),
    [selectedFolders, section]
  )

  const tabItems = useMemo(
    () => FOLDER_KEYS.map((key) => ({ key, label: formatFolderLabel(key) })),
    []
  )

  // True when the vehicle has no folder data at all and no trash files.
  const hasNoData = useMemo(
    () =>
      !FOLDER_KEYS.some((key) => promotedFolderData?.[key]?.length > 0) &&
      trashFiles?.length === 0,
    [promotedFolderData, trashFiles]
  )

  // Keep the active tab valid when the available folders change.
  useEffect(() => {
    if (tabItems.length === 0) {
      setSection(null)
      return
    }
    if (!tabItems.some((item) => item.key === section)) {
      setSection(tabItems[0].key)
    }
  }, [tabItems, section])

  const selectDeselectFolders = (file_name, folder_name) => {
    setSelectedFolders((prev) => {
      const temp = { ...prev }
      const idx = temp[folder_name].indexOf(file_name)
      if (idx > -1) {
        temp[folder_name] = temp[folder_name].filter(
          (name) => name !== file_name
        )
      } else {
        temp[folder_name] = [...temp[folder_name], file_name]
      }
      return temp
    })
  }

  const toggleSelectAll = (checked) => {
    setSelectedFolders((prev) => ({
      ...prev,
      [section]: checked ? [...(promotedFolderData?.[section] || [])] : [],
    }))
  }

  const allSectionSelected =
    section && promotedFolderData?.[section]?.length > 0
      ? selectedFolders?.[section]?.length ===
        promotedFolderData?.[section]?.length
      : false

  const hasSelections = FOLDER_KEYS.some(
    (key) => selectedFolders?.[key]?.length > 0
  )

  const deleteSelectedFilesAndFolders = () => {
    modal.confirm({
      title: intl.formatMessage({
        id: 'promoteChanges.deleteSelectedFilesTitle',
      }),
      icon: <ExclamationCircleOutlined />,
      content: intl.formatMessage({
        id: 'promoteChanges.deleteSelectedFilesConfirm',
      }),
      centered: true,
      maskClosable: false,
      okText: intl.formatMessage({ id: 'promoteChanges.delete' }),
      okType: 'danger',
      cancelText: intl.formatMessage({ id: 'promoteChanges.cancel' }),
      onOk: async () => {
        navDispatch({
          type: 'loader',
          data: {
            caption: intl.formatMessage({
              id: 'promoteChanges.deletingSelectedFiles',
            }),
            showLoader: true,
          },
          notification: {},
        })

        let payload = {
          selected_folder: selectedFolders,
        }
        let res = await postRequest(
          `/api/delete/promoted-files/run/${vehicleId}/`,
          payload
        )
        if (res?.status) {
          navDispatch({
            type: 'notification',
            data: {
              notification_type: 'success',
              msg: intl.formatMessage({ id: 'promoteChanges.filesDeleted' }),
            },
          })
          setSelectedFolders(emptyFolders())
          setReview(false)
          await loadDetails()
          onDeleted()
        } else {
          navDispatch({
            type: 'notification',
            data: {
              notification_type: 'error',
              msg: intl.formatMessage({ id: 'promoteChanges.deleteFilesError' }),
            },
          })
        }
      },
    })
  }

  const handleTrashDeleted = () => {
    onDeleted()
    loadDetails()
  }

  const renderFileGrid = () => {
    if (!section) return null
    return (
      <Flex vertical gap={12} className="promote-delete-file-grid">
        <Flex align="center" gap={16} wrap>
          {!isReview && promotedFolderData?.[section]?.length > 0 && (
            <>
              <Checkbox
                checked={allSectionSelected}
                onChange={(e) => toggleSelectAll(e.target.checked)}
              >
                {intl.formatMessage({ id: 'promoteChanges.selectAll' })}
              </Checkbox>
              <Text type="secondary">
                {intl.formatMessage(
                  { id: 'promoteChanges.selectedCount' },
                  {
                    selected: selectedFolders?.[section]?.length || 0,
                    total: promotedFolderData?.[section]?.length || 0,
                  }
                )}
              </Text>
            </>
          )}
          <InputText
            name={`search_${section}`}
            placeholder={intl.formatMessage(
              { id: 'promoteChanges.searchSection' },
              { section: formatFolderLabel(section) }
            )}
            type="search"
            value={searchValue}
            isRequired={false}
            onChange={setSearchValue}
            wrapperClass="promote-section-search"
          />
        </Flex>
        <div className="promote-section-files-list">
          <Flex wrap="wrap" gap={8} className="promote-files-grid">
            {filteredFiles.map((file_name, idx) => (
              <div
                key={`${section}-${file_name}-${idx}`}
                className="promote-file-check-item"
              >
                {isReview ? (
                  <>
                    <CheckCircleFilled style={{ color: 'green' }} />
                    <EllipsisTooltip className="ellipsis">
                      {file_name}
                    </EllipsisTooltip>
                  </>
                ) : (
                  <Checkbox
                    checked={selectedFolderSet.has(file_name)}
                    onChange={() => selectDeselectFolders(file_name, section)}
                  >
                    <EllipsisTooltip className="ellipsis">
                      {file_name}
                    </EllipsisTooltip>
                  </Checkbox>
                )}
              </div>
            ))}
          </Flex>
          {filteredFiles.length === 0 && (
            <Flex
              align="center"
              justify="center"
              style={{ width: '100%', padding: '24px' }}
            >
              <Empty
                image={Empty.PRESENTED_IMAGE_SIMPLE}
                description={intl.formatMessage({
                  id:
                    isReview && selectedFolders?.[section]?.length === 0
                      ? 'promoteChanges.noFilesSelected'
                      : 'promoteChanges.noRecords',
                })}
              />
            </Flex>
          )}
        </div>
      </Flex>
    )
  }

  const renderFooter = () => (
    <Flex
      align="center"
      justify="space-between"
      wrap
      gap={12}
      className="promote-modal-footer"
    >
      <div>
        {section && (
          <Text>
            {intl.formatMessage(
              { id: 'promoteChanges.totalSelected' },
              {
                section: formatFolderLabel(section),
                count: selectedFolders?.[section]?.length || 0,
              }
            )}
          </Text>
        )}
      </div>
      <Flex align="center" gap={12} wrap>
        {isReview ? (
          <>
            <Button onClick={() => setReview(false)}>
              {intl.formatMessage({ id: 'promoteChanges.reSelectFiles' })}
            </Button>
            <Button
              type="primary"
              danger
              disabled={!hasSelections}
              onClick={deleteSelectedFilesAndFolders}
            >
              {intl.formatMessage({ id: 'promoteChanges.deleteSelectedFiles' })}
            </Button>
          </>
        ) : (
          <Button
            type="primary"
            disabled={!hasSelections}
            onClick={() => setReview(true)}
          >
            {intl.formatMessage({ id: 'promoteChanges.review' })}
          </Button>
        )}
      </Flex>
    </Flex>
  )

  const renderBody = () => {
    if (branchExists === null || loadingDetails) {
      return (
        <Flex align="center" justify="center" style={{ padding: '32px' }}>
          <ProgresSpinner
            caption={intl.formatMessage({ id: 'promoteChanges.loadingData' })}
          />
        </Flex>
      )
    }

    return (
      <Flex vertical gap={0}>
        {branchExists ? (
          hasNoData ? (
            <Flex align="center" justify="center" style={{ padding: '24px' }}>
              <Result
                status="warning"
                title={intl.formatMessage({
                  id: 'promoteChanges.dataNotFoundTitle',
                })}
                subTitle={intl.formatMessage({
                  id: 'promoteChanges.dataNotFoundSubTitle',
                })}
              />
            </Flex>
          ) : (
            <>
              <BoxTabs
                options={tabItems.map(({ key }) => ({
                  label: formatFolderLabel(key),
                  value: key,
                }))}
                value={section || tabItems[0]?.key}
                onChange={(e) => setSection(e.target.value)}
              />
              {renderFileGrid()}
              {renderFooter()}
            </>
          )
        ) : (
          <Result
            status="warning"
            title={intl.formatMessage({
              id: 'promoteChanges.branchNotFoundTitle',
            })}
            subTitle={intl.formatMessage({
              id: 'promoteChanges.branchNotFoundSubTitle',
            })}
          />
        )}
      </Flex>
    )
  }

  return (
    <>
      {contextHolder}
      <Modal
        title={intl.formatMessage(
          { id: 'promoteChanges.deleteFilesTitle' },
          { name: vehicle?.name || '' }
        )}
        open={!!vehicle}
        centered
        maskClosable={false}
        onCancel={onClose}
        width="80%"
        footer={null}
        className="promote-changes-delete-files-modal"
        destroyOnHidden
        extra={
          !loadingDetails && (isExists || trashFiles?.length > 0) ? (
            <Flex align="center" gap={8} wrap>
              {isExists && (
                <DownloadDeleteFileLogBtn isBtn vehicle={vehicleId} />
              )}
              {trashFiles?.length > 0 && (
                <Button onClick={() => setShowTrashedModal(true)}>
                  {intl.formatMessage({
                    id: 'promoteChanges.deleteTrashedFiles',
                  })}
                </Button>
              )}
            </Flex>
          ) : null
        }
      >
        {renderBody()}
      </Modal>

      {showTrashedModal && (
        <SelectTrashFilesModal
          vehicle={vehicleId}
          open={showTrashedModal}
          trashFiles={trashFiles}
          branchExists={branchExists}
          selectedTrashFiles={selectedTrashFiles}
          setSelectedTrashFiles={setSelectedTrashFiles}
          onClose={() => {
            setShowTrashedModal(false)
            setSelectedTrashFiles([])
          }}
          onDeleted={handleTrashDeleted}
        />
      )}
    </>
  )
}

export default DeleteFilesModal
